import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { deploymentModels, products } from '../data/productRegistry';
import { useLocalizedProducts, useLocalizedDeployments } from '../hooks/useLocalizedProducts';

const SolutionsPage = () => {
  const { t } = useTranslation('editor');
  const localizedProducts = useLocalizedProducts();
  const localizedDeployments = useLocalizedDeployments();

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-70" />
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[440px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/25 to-emerald-500/20 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
              <span className="text-cyan-200 text-sm font-semibold tracking-wide">{t('solutionsPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-gradient animate-shimmer">{t('solutionsPage.title')}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-400 max-w-2xl mx-auto">
              {t('solutionsPage.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {deploymentModels.map((dep, i) => {
            const DepIcon = dep.icon;
            const locDep = localizedDeployments.find((d) => d.id === dep.id);
            const availableProducts = products.filter((p) => p.deployments.includes(dep.id));
            return (
              <motion.div
                key={dep.id}
                id={dep.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="scroll-mt-40 rounded-3xl glass-card overflow-hidden hover:border-cyan-300/30 transition-colors"
              >
                <div className={`h-1 bg-gradient-to-r ${dep.accent}`} />
                <div className="p-8 grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${dep.accent} mb-4`}>
                      <DepIcon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-white mb-2">{locDep?.name ?? dep.name}</h2>
                    <p className="text-slate-400 leading-relaxed mb-4">{locDep?.description ?? dep.description}</p>
                    <ul className="space-y-2">
                      {(locDep?.features ?? dep.features).map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2 text-sm text-slate-200">
                          <Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:col-span-2">
                    <h3 className="text-sm font-semibold text-cyan-300/80 uppercase tracking-wider mb-4">
                      {t('solutionsPage.availableProducts')}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {availableProducts.map((p) => {
                        const locP = localizedProducts.find((lp) => lp.slug === p.slug);
                        return (
                          <Link
                            key={p.slug}
                            to={`/products/${p.slug}`}
                            className="group flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.03] hover:border-cyan-300/40 hover:bg-white/[0.07] transition-all"
                          >
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${p.accent}`}>
                              <p.icon className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-white group-hover:text-cyan-200 truncate">
                                {locP?.name ?? p.name}
                              </div>
                              <div className="text-xs text-slate-400 truncate">{locP?.tagline ?? p.tagline}</div>
                            </div>
                            <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-1 transition-all" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
